/* eslint-disable @typescript-eslint/no-magic-numbers */
import {
  OnInit,
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
} from '@angular/core';

import cloneDeep from 'lodash/cloneDeep';
import {
  concatMap,
  Observable,
  Subscription,
  tap,
} from 'rxjs';
import { NgbCalendar, NgbDateAdapter } from '@ng-bootstrap/ng-bootstrap';
import { Chart, ChartData, ChartOptions } from 'chart.js';
import moment from 'moment';
import { Moment } from 'moment-timezone';

import { ServiceSettings, ResourceSettings } from '@shared/restaurant/models';
import { AnalyticsService as AnalyticsSharedService } from '@shared/analytics/services';
import {
  Analytics,
  Metrics,
  Statistics,
  ServicesResourcesData,
} from '@shared/analytics/models';
import {
  CapitalizePipe,
  CommonRadioButtonItem,
  PageTitleService,
} from '@tablesready/common-ui';
import { PartiesService } from '../services';
import { AnalyticsApiService } from './services';
import { DEFAULT_PLOT_DATA, DEFAULT_PLOT_OPTIONS, ILabels } from './analytics.interfaces';
import { ServicesAndResourcesRadioItems } from './enums';
import { DateOptions } from './models';
import {
  StatisticsComponent,
  ServicesAndResourcesStatisticsComponent,
} from './components';
import { HeaderTitleService } from '@tablesready/frontend-header';
import { AuthRestaurantService } from '@tablesready/frontend-store';
import { AlertsService } from '@tablesready/frontend-alerts-bar';
import { IonContent } from '@ionic/angular';

const WEEKS_TO_GO_BACK = 13;
const DEFAULT_HOURLY = 6;

@Component({
  selector: 'tablesready-root',
  templateUrl: './analytics.component.html',
  styleUrls: ['./analytics.component.scss'],
})
export class AnalyticsComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('weeklyChartRef') weeklyChartRef: ElementRef<HTMLCanvasElement>;
  @ViewChild('dailyChartRef') dailyChartRef: ElementRef<HTMLCanvasElement>;
  @ViewChild('hourlyChartRef') hourlyChartRef: ElementRef<HTMLCanvasElement>;
  @ViewChild('statisticsComponent', { read: StatisticsComponent }) statisticsComponent: StatisticsComponent;
  @ViewChild('servicesResourcesStatisticsComponent', { read: ServicesAndResourcesStatisticsComponent }) servicesResourcesStatisticsComponent: ServicesAndResourcesStatisticsComponent;
  @ViewChild(IonContent) ionContent?: IonContent;

  userName: string;
  labels: ILabels;
  size: boolean;
  analytics: Analytics;
  fromDate: string;
  toDate: string;
  dateOptions: DateOptions;
  timezone: string;
  serviceLabel: string = 'service';
  resourceLabel: string = 'resource';
  weeksObjs: Metrics[];
  daysObjs: Metrics[];
  hoursObjs: Metrics[][];
  statistics: Statistics[];
  servicesResourcesStatistics: ServicesResourcesData[];
  servicesResourcesRadioItems: CommonRadioButtonItem[] = [];
  weeklyRadioItems: CommonRadioButtonItem[] = [];
  dailyRadioItems: CommonRadioButtonItem[] = [];
  hourlyRadioItems: CommonRadioButtonItem[] = [];
  daysRadioItems: CommonRadioButtonItem[] = [];
  servicesSettings: ServiceSettings;
  resourcesSettings: ResourceSettings;

  weeklyChartType = 'avWaitTime';
  dailyChartType = 'avWaitTime';
  hourlyChartType = 'avWaitTime';
  hourlyDOW = DEFAULT_HOURLY;

  weeklyChart: Chart<'line', number[], string>;
  dailyChart: Chart<'line', number[], string>;
  hourlyChart: Chart<'line', number[], string>;

  private readonly subscriptions = new Subscription();

  // eslint-disable-next-line max-params
  constructor(
    private readonly calendar: NgbCalendar,
    private readonly dateAdapter: NgbDateAdapter<string>,
    private readonly authRestaurantService: AuthRestaurantService,
    private readonly alertsService: AlertsService,
    private readonly partiesService: PartiesService,
    private readonly analyticsApiService: AnalyticsApiService,
    private readonly analyticsSharedService: AnalyticsSharedService,
    private readonly pageTitleService: PageTitleService,
    private readonly capitalize: CapitalizePipe,
    private readonly headerService: HeaderTitleService,
  ) {}

  get weeksToGoBack(): number {
    return WEEKS_TO_GO_BACK;
  }

  ionViewWillEnter(): void {
    void this.ionContent?.scrollToTop(0);
    this.weeklyChart?.resize();
    this.dailyChart?.resize();
    this.hourlyChart?.resize();
  }

  ionViewWillLeave(): void {
    this.setTodayDate();
    this.resetPageState();
    this.servicesResourcesStatisticsComponent?.resetState();
  }

  ngOnInit(): void {
    this.setTodayDate();
    this.dateOptions = {
      minDate: this.calendar.getPrev(this.calendar.getToday(), 'd', 7 * 13),
      maxDate: this.calendar.getToday(),
    };
  }

  // eslint-disable-next-line max-lines-per-function
  ngAfterViewInit(): void {
    this.weeklyChart = new Chart(this.weeklyChartRef.nativeElement, {
      type: 'line',
      data: cloneDeep(DEFAULT_PLOT_DATA),
      options: cloneDeep(DEFAULT_PLOT_OPTIONS),
    });

    this.dailyChart = new Chart(this.dailyChartRef.nativeElement, {
      type: 'line',
      data: cloneDeep(DEFAULT_PLOT_DATA),
      options: cloneDeep(DEFAULT_PLOT_OPTIONS),
    });

    this.hourlyChart = new Chart(this.hourlyChartRef.nativeElement, {
      type: 'line',
      data: cloneDeep(DEFAULT_PLOT_DATA),
      options: cloneDeep(DEFAULT_PLOT_OPTIONS),
    });

    this.dailyChart.data.labels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    this.dailyChart.data.datasets[0].data = [0, 0, 0, 0, 0, 0, 0];
    this.dailyChart.data.datasets[1].data = [0, 0, 0, 0, 0, 0, 0];

    const HOURS_IN_TWO_DAYS = 48;
    const daytimeLabels = this.generateDaytimeLabels();
    const hourlyDataSet: number[] = [];
    for (let i = 0; i <= HOURS_IN_TWO_DAYS; i++) {
      hourlyDataSet.push(0);
    }

    this.hourlyChart.data.labels = daytimeLabels;
    this.hourlyChart.data.datasets[0].data = [...hourlyDataSet];
    this.hourlyChart.data.datasets[1].data = [...hourlyDataSet];

    this.pageTitleService.setTitle('Analytics');

    const subscription = this.authRestaurantService.selectAndDetectLocationChanges()
      .pipe(
        tap(({ location: user }) => {
          this.servicesSettings = user.serviceSettings;
          this.resourcesSettings = user.resourceSettings;

          this.userName = user.name;
          this.timezone = user.timezone;
          this.size = user?.waitFields?.size;
          this.labels = user?.displayOptions?.labels;
          this.serviceLabel = user?.serviceSettings?.label;
          this.resourceLabel = user?.resourceSettings.label;

          const title = `${user.name} Analytics`;
          this.headerService.setDataForRoute('analytics', { title });
        }),
        concatMap(() => this.analyticsApiService.load()),
        tap(analytics => {
          this.analytics = analytics;
          this.statistics = analytics.statistics;
          this.servicesResourcesStatistics = analytics.servicesResourcesStatistics;

          this.initPlotData(analytics);
          this.generateWeeklyLabels();
          this.setRadioItems();
          this.updateAnalytics();
        }),
      )
      .subscribe();
    this.subscriptions.add(subscription);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  updateWeeklyPlot(type: string): void {
    this.updatePlotOptions(this.weeklyChart.options, this.weeklyChart.data, type);
    this.updatePlotData(this.weeksObjs, this.weeklyChart.data, type);

    this.weeklyChart.update();
  }

  updateDailyPlot(type: string): void {
    this.updatePlotOptions(this.dailyChart.options, this.dailyChart.data, type);
    this.updatePlotData(this.daysObjs, this.dailyChart.data, type);

    this.dailyChart.update();
  }

  updateHourlyPlot(type: string, dayType: number): void {
    const hourDayObj = this.hoursObjs[dayType];
    this.updatePlotOptions(this.hourlyChart.options, this.hourlyChart.data, type);
    this.updatePlotData(hourDayObj, this.hourlyChart.data, type);

    this.hourlyChart.update();
  }

  simpleDateToMoment = (date: string): Moment => {
    return moment.tz(date, 'D-M-YYYY', this.timezone);
  }

  formatDateToTimestamp = (date: string, text: string): number => {
    const _date = this.simpleDateToMoment(date);
    if (text === 'from') {
      _date.set({
        hour: 0,
        minute: 0,
        second: 0,
        millisecond: 0
      });
    } else {
      _date.set({
        hour: 23,
        minute: 59,
        second: 59,
        millisecond: 999,
      });
    }

    return _date.valueOf();
  }

  loadTodayStats = (): Observable<Statistics> => {
    return this.analyticsApiService.loadTodayStats();
  }

  updateAnalytics(): void {
    this.statisticsComponent.updateAnalytics();
    this.servicesResourcesStatisticsComponent?.updateAnalytics();
  }

  setTodayDate(): void {
    this.fromDate = this.dateAdapter.toModel(this.calendar.getToday());
    this.toDate = this.fromDate;
  }

  private initPlotData(data: Analytics): void {
    const { weeks, days, hours } = data.plot;
    this.weeksObjs = weeks;
    this.daysObjs = days;
    this.hoursObjs = hours;
  }

  // eslint-disable-next-line max-lines-per-function,complexity
  private updatePlotOptions(
    plotOptionsRef: ChartOptions<'line'>,
    plotDataRef: ChartData<'line'>,
    graphType: string,
  ): void {
    plotDataRef.datasets = plotDataRef.datasets.slice(0, 2);
    const customerLabel = this.labels.customer_plural[0].toUpperCase() + this.labels.customer_plural.substring(1);
    plotOptionsRef.scales.x.grid.borderColor = 'gray';

    switch (graphType) {
      case 'avWaitTime':
        plotDataRef.datasets[0].label = 'Avg Wait Time';
        plotDataRef.datasets[1].label = 'Avg Time from Notification to Being Served';
        plotDataRef.datasets[1].yAxisID = 'y-axis-0';
        plotOptionsRef.plugins.legend.display = true;
        plotOptionsRef.scales['y-axis-0'] = {
          display: true,
          position: 'left',
          grid: {
            drawOnChartArea: false,
            color: 'gray',
            borderColor: 'gray',
          },
          title: {
            display: true,
            text: 'Minutes',
          },
        };
        plotOptionsRef.scales['y-axis-1'] = {
          display: false,
          position: 'right',
          grid: {
            drawOnChartArea: false,
            color: 'gray',
            borderColor: 'gray',
          },
          title: {
            display: true,
            text: 'Minutes',
          },
        };
        break;
      case 'numOfPartiesDinners':
        // First series, all domains
        plotDataRef.datasets[0].label = customerLabel;
        plotDataRef.datasets[1].label = '';
        plotOptionsRef.plugins.legend.display = false;
        plotOptionsRef.scales['y-axis-0'] = {
          type: 'linear',
          display: true,
          position: 'left',
          grid: {
            drawOnChartArea: false,
            borderColor: 'gray',
          },
          title: {
            display: true,
            text: customerLabel,
          },
        };
        plotOptionsRef.scales['y-axis-1'] = {
          type: 'linear',
          display: false,
          position: 'right',
          grid: {
            drawOnChartArea: false,
            borderColor: 'gray',
          },
          title: {
            display: true,
            text: '',
          },
        };

        if (this.size) {
          plotOptionsRef.plugins.legend.display = true;
          plotDataRef.datasets[1].label = 'Total Quantity';
          plotOptionsRef.scales['y-axis-1'].display = true;
          plotOptionsRef.scales['y-axis-1'].title.text = 'Total Quantity';
          plotDataRef.datasets[1].yAxisID = 'y-axis-1';
        }
        break;
      case 'numOfNotifications':
        plotDataRef.datasets[0].label = 'Outbound SMS';
        plotDataRef.datasets[1].label = 'Calls';
        plotDataRef.datasets[1].yAxisID = 'y-axis-0';
        plotOptionsRef.plugins.legend.display = true;
        plotDataRef.datasets[2] = {
          data: [0, 0, 0, 0, 0, 0, 0, 0],
          label: 'Inbound SMS',
          yAxisID: 'y-axis-0',
        };

        plotOptionsRef.scales['y-axis-0'] = {
          type: 'linear',
          display: true,
          position: 'left',
          grid: {
            drawOnChartArea: false,
            borderColor: 'gray',
          },
          title: {
            display: false,
            text: 'Notifications',
          },
        };
        plotOptionsRef.scales['y-axis-1'] = {
          type: 'linear',
          display: false,
          position: 'right',
          grid: {
            drawOnChartArea: false,
            borderColor: 'gray',
          },
          title: {
            display: true,
            text: '',
          },
        };
        plotOptionsRef.scales['y-axis-2'] = {
          type: 'linear',
          display: false,
          position: 'right',
          grid: {
            drawOnChartArea: false,
            borderColor: 'gray',
          },
          title: {
            display: true,
            text: '',
          },
        };
        break;
      case 'avgTurnTime':
        plotDataRef.datasets[0].label = 'Average turn time';
        plotDataRef.datasets[1].yAxisID = 'y-axis-0';
        plotOptionsRef.plugins.legend.display = false;
        plotOptionsRef.scales['y-axis-0'] = {
          type: 'linear',
          display: true,
          position: 'left',
          grid: {
            drawOnChartArea: false,
            borderColor: 'gray',
          },
          title: {
            display: true,
            text: 'Minutes',
          },
        };
        plotOptionsRef.scales['y-axis-1'] = {
          type: 'linear',
          display: false,
          position: 'right',
          grid: {
            drawOnChartArea: false,
            borderColor: 'gray',
          },
          title: {
            display: true,
            text: '',
          },
        };
        break;
      case 'onTimeSeatingPercentage':
        plotDataRef.datasets[0].label = 'On Time Served Percentage';
        plotDataRef.datasets[1].yAxisID = 'y-axis-0';
        plotOptionsRef.plugins.legend.display = false;
        plotOptionsRef.scales['y-axis-0'] = {
          type: 'linear',
          display: true,
          position: 'left',
          grid: {
            drawOnChartArea: false,
            borderColor: 'gray',
          },
          title: {
            display: true,
            text: 'Percent',
          },
        };
        plotOptionsRef.scales['y-axis-1'] = {
          type: 'linear',
          display: false,
          position: 'right',
          grid: {
            drawOnChartArea: false,
            borderColor: 'gray',
          },
          title: {
            display: true,
            text: '',
          },
        };
        break;
      case 'canceledPercentage':
        plotDataRef.datasets[0].label = 'Canceled Percentage';
        plotDataRef.datasets[1].yAxisID = 'y-axis-0';
        plotOptionsRef.plugins.legend.display = false;
        plotOptionsRef.scales['y-axis-0'] = {
          type: 'linear',
          display: true,
          position: 'left',
          grid: {
            drawOnChartArea: false,
            borderColor: 'gray',
          },
          title: {
            display: true,
            text: 'Percent',
          },
        };
        plotOptionsRef.scales['y-axis-1'] = {
          type: 'linear',
          display: false,
          position: 'right',
          grid: {
            drawOnChartArea: false,
            borderColor: 'gray',
          },
          title: {
            display: true,
            text: '',
          },
        };
        break;
      default:
        break;
    }
  }

  private updatePlotData(
    seriesObjs: Metrics[],
    plotDataRef: ChartData<'line'>,
    graphType: string,
  ): void {
    if (typeof seriesObjs !== 'undefined') {
      // eslint-disable-next-line complexity
      seriesObjs.forEach((seriesObj, index) => {
        switch (graphType || 'avWaitTime') {
          case 'avWaitTime':
            plotDataRef.datasets[0].data[index] = seriesObj.avgWait;
            plotDataRef.datasets[1].data[index] = seriesObj.avgResponseTime;
            break;
          case 'numOfPartiesDinners':
            plotDataRef.datasets[0].data[index] = seriesObj.totalParties;
            plotDataRef.datasets[1].data[index] = this.size ? seriesObj.totalCustomers : null;
            break;
          case 'numOfNotifications':
            plotDataRef.datasets[0].data[index] = seriesObj.totalSms;
            plotDataRef.datasets[1].data[index] = seriesObj.totalVoice;
            plotDataRef.datasets[2].data[index] = seriesObj.totalInbound;
            break;
          case 'avgTurnTime':
            plotDataRef.datasets[0].data[index] = seriesObj.avgTurnTime;
            plotDataRef.datasets[1].data[index] = null;
            break;
          case 'onTimeSeatingPercentage':
            plotDataRef.datasets[0].data[index] = seriesObj.pctOnTime;
            plotDataRef.datasets[1].data[index] = null;
            break;
          case 'canceledPercentage':
            plotDataRef.datasets[0].data[index] = seriesObj.pctCanceled;
            plotDataRef.datasets[1].data[index] = null;
            break;
          default:
            break;
        }
      });
    }
  }

  private setRadioItems(): void {
    const partiesName = this.size ? '<span>and quantity </span>' : '';
    const items = [
      {
        name: `Avg wait and ${this.labels.customer} response times`,
        value: 'avWaitTime',
      },
      {
        name: `${this.capitalize.transform(this.labels.customer_plural)} ${partiesName} served`,
        value: 'numOfPartiesDinners',
      },
      {
        name: 'Avg turn time',
        value: 'avgTurnTime',
      },
      {
        name: 'Notifications sent',
        value: 'numOfNotifications',
      },
      {
        name: 'On-time served %',
        value: 'onTimeSeatingPercentage',
      },
      {
        name: 'Canceled %',
        value: 'canceledPercentage',
      },
    ];

    this.weeklyRadioItems = cloneDeep(items);
    this.dailyRadioItems = cloneDeep(items);
    this.hourlyRadioItems = cloneDeep(items);

    this.daysRadioItems = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => ({
      name: day,
      value: index,
    }));

    this.servicesResourcesRadioItems = [
      {
        name: `${this.serviceLabel}`,
        value: ServicesAndResourcesRadioItems.services,
      },
      {
        name: `${this.resourceLabel}`,
        value: ServicesAndResourcesRadioItems.resources,
      },
      {
        name: `${this.serviceLabel} & ${this.resourceLabel}`,
        value: ServicesAndResourcesRadioItems.servicesAndResources,
      },
    ];

    setTimeout(() => {
      this.updateWeeklyPlot(this.weeklyChartType);
      this.updateDailyPlot(this.dailyChartType);
      this.updateHourlyPlot(this.hourlyChartType, this.hourlyDOW);
    });
  }

  private generateDaytimeLabels(): string[] {
    const HOURS_IN_A_DAY = 24;
    const HOURS_BEFORE_NOON = 12;
    const daytimeLabels: string[] = [];

    for (let i = 0; i < HOURS_IN_A_DAY; i++) {
      const isMorning = i <= HOURS_BEFORE_NOON;
      let label = '';

      if (isMorning && i === 0) {
        label = 'Midnight';
      } else if (isMorning && i === HOURS_BEFORE_NOON) {
        label = 'Noon';
      } else if (isMorning) {
        label = `${i}am`;
      } else {
        label = `${i - HOURS_BEFORE_NOON}pm`;
      }

      daytimeLabels.push(label);
      daytimeLabels.push('');
    }

    return daytimeLabels;
  }

  private generateWeeklyLabels(): void {
    const startOfToday = moment().tz(this.timezone)
      .startOf('day');

    const from = startOfToday.clone().subtract(this.weeksToGoBack - 1, 'week');
    const to = startOfToday.add(1, 'day');
    const newLabels: string[] = [];

    do {
      newLabels.push(from.format('M/D/YY'));
    } while (from.add(1, 'week').valueOf() < to.valueOf());

    this.weeklyChart.data.labels = newLabels;
    this.weeklyChart.update();
  }

  private resetPageState(): void {
    this.weeklyChartType = 'avWaitTime';
    this.dailyChartType = 'avWaitTime';
    this.hourlyChartType = 'avWaitTime';
    this.hourlyDOW = DEFAULT_HOURLY;
  }
}
